import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '../../systems/motion.ts';
import styles from './PhaseTransition.module.css';

export type Cut = 'ink' | 'curtain' | 'shutter' | 'tear';

/** Which cut goes with which change of phase. */
export function cutFor(from: string, to: string): Cut {
  if (to === 'reveal' || to === 'ended') return 'tear';
  if (to === 'hideout') return 'shutter';
  if (from === 'planning' && (to === 'execution' || to === 'escape')) return 'curtain';
  return 'ink';
}

/**
 * Plays a cut over the screen when the phase changes (not when the game first shows). It only
 * uncovers the new phase, which is already under it; with "reduce motion" there is no cut.
 */
export function PhaseTransition({ phase }: { phase: string }) {
  const reduced = useReducedMotion();
  const last = useRef(phase);
  const [cut, setCut] = useState<{ kind: Cut; n: number } | null>(null);

  useEffect(() => {
    if (phase === last.current) return;
    const kind = cutFor(last.current, phase);
    last.current = phase;
    if (!reduced) setCut((c) => ({ kind, n: (c?.n ?? 0) + 1 }));
  }, [phase, reduced]);

  if (!cut) return null;
  const done = (): void => {
    setCut(null);
  };
  if (cut.kind === 'curtain') {
    return (
      <div className={styles['layer']} aria-hidden="true" key={cut.n}>
        <div className={styles['curtainL']} />
        <div className={styles['curtainR']} onAnimationEnd={done} />
      </div>
    );
  }
  return (
    <div
      className={[styles['layer'], styles[cut.kind]].join(' ')}
      aria-hidden="true"
      key={cut.n}
      onAnimationEnd={done}
    />
  );
}
